@if (layout() === 'callback') {
  <div
    class="npf-callback-card flex w-full max-w-lg flex-col gap-6 rounded-lg border border-gray-200 bg-white p-6 shadow-lg">
    <div class="flex flex-col items-center text-center">
      <div class="mb-3 flex h-12 w-12 items-center justify-center rounded-full border-2 border-primary-600">
        <span class="text-primary-600 text-xl font-bold" aria-hidden="true">&#10003;</span>
      </div>
      <h1 id="welcome-notification-title" class="text-xl font-semibold text-gray-900">
        Welcome to the new portal!
      </h1>
      <p class="mt-2 text-sm text-gray-600">
        @if (displayCompanyName(); as name) {
          Choose which email notifications you would like to receive for
          <span class="font-semibold text-gray-900">{{ name }}</span>.
        } @else {
          Your account was migrated from the legacy portal. Choose which email notifications you would like to receive.
        }
      </p>
    </div>

    @if (displayCompanyName(); as name) {
      <div class="npf-company-callout" role="note">
        <p class="npf-company-callout-eyebrow">Setting up notifications for</p>
        <p class="npf-company-callout-name">{{ name }}</p>
        <p class="npf-company-callout-detail">
          These choices apply only to this company.
          @if (hasMultipleCompanies()) {
            To manage notifications for a different company, switch companies using the selector at the top of the portal,
            then open
            <span class="npf-company-callout-emphasis">Settings</span>
            and update preferences there.
          }
        </p>
      </div>
    }

    @if (isLoading()) {
      <p class="text-center text-sm text-gray-600">Loading notification preferences…</p>
    } @else if (!hasAnySection()) {
      <p class="text-center text-sm text-gray-600">
        No notification types are available for the selected company with your current role.
      </p>
    } @else {
      <ng-container *ngTemplateOutlet="toggleFields" />
    }

    @if (errorMessage()) {
      <p class="text-center text-sm text-red-600">{{ errorMessage() }}</p>
    }

    <button
      mat-flat-button
      color="primary"
      type="button"
      class="w-full"
      [disabled]="saveDisabled()"
      (click)="save.emit()">
      {{ isSaving() ? 'Saving…' : 'Save preferences' }}
    </button>
    @if (showSkip()) {
      <button
        mat-stroked-button
        type="button"
        class="w-full"
        [disabled]="isSaving() || isLoading()"
        (click)="skip.emit()">
        Skip notification setup
      </button>
    }
  </div>
} @else {
  <div class="npf-embedded flex flex-col gap-6 min-w-0">
    <mat-card class="npf-card">
      <mat-card-content class="npf-card-content">
        <h2 class="npf-section-title">Email Notifications</h2>
        <p class="npf-section-intro text-sm text-gray-600 mb-4">
          @if (displayCompanyName(); as companyName) {
            Notification preferences for {{ companyName }}.
          } @else {
            Choose which notifications you want to receive. Options you are not allowed to change are controlled by your role.
          }
        </p>
        @if (isLoading()) {
          <p class="text-sm text-gray-600 mb-4">Loading notification preferences…</p>
        } @else if (!hasAnySection()) {
          <p class="text-sm text-gray-600 mb-4">
            No notification types are available for the selected company with your current role.
          </p>
        } @else {
          <ng-container *ngTemplateOutlet="toggleFields" />
        }
        @if (errorMessage()) {
          <p class="mt-4 text-sm text-red-600">{{ errorMessage() }}</p>
        }
        <div class="npf-actions">
          <button
            mat-flat-button
            color="primary"
            type="button"
            [disabled]="saveDisabled()"
            (click)="save.emit()">
            {{ isSaving() ? 'Saving…' : 'Save changes' }}
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
}

<ng-template #toggleFields>
  <div class="flex flex-col gap-4" [formGroup]="form()">
    @if (showSection('billing')) {
      <div
        class="flex flex-col gap-2 rounded-lg border border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="min-w-0">
          <div class="font-medium text-gray-900">Billing notifications</div>
          <div class="text-sm text-gray-600">Receive email when a new invoice is generated.</div>
        </div>
        <mat-slide-toggle color="primary" formControlName="billing" />
      </div>
    }

    @if (showSection('enrollment')) {
      <div
        class="flex flex-col gap-2 rounded-lg border border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="min-w-0">
          <div class="font-medium text-gray-900">Enrollment notifications</div>
          <div class="text-sm text-gray-600">Receive email when a new enrollment is submitted.</div>
        </div>
        <mat-slide-toggle color="primary" formControlName="enrollment" />
      </div>
    }

    @if (showSection('cancellation')) {
      <div
        class="flex flex-col gap-2 rounded-lg border border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="min-w-0">
          <div class="font-medium text-gray-900">Cancellation notifications</div>
          <div class="text-sm text-gray-600">Receive email when a member cancellation occurs.</div>
        </div>
        <mat-slide-toggle color="primary" formControlName="cancellation" />
      </div>
    }
  </div>
</ng-template>
